import React, { useEffect, useRef, useState } from "react";
import { useLang } from "../lib/lang-context";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";

// Counts up to a numeric target. If target is non-numeric (e.g. "MX · US · CA"), shows as-is.
const Counter = ({ target, suffix = "" }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const numericTarget = typeof target === "number" ? target : NaN;
  const isNumeric = !Number.isNaN(numericTarget);

  useEffect(() => {
    if (!isNumeric) return;
    let raf;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const start = performance.now();
            const duration = 1400;
            const animate = (now) => {
              const t = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - t, 3);
              setVal(Math.round(numericTarget * eased));
              if (t < 1) raf = requestAnimationFrame(animate);
            };
            raf = requestAnimationFrame(animate);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isNumeric, numericTarget]);

  return (
    <span ref={ref}>
      {isNumeric ? val : target}
      {suffix}
    </span>
  );
};

// Parse stat value: if matches "24-72" treat as range animated to 72; if pure number animate, else as-is.
const renderStat = (value) => {
  if (typeof value !== "string") return <Counter target={value} />;
  const numMatch = value.match(/^(\+?)(\d+)([,\d]*)$/);
  const rangeMatch = value.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (rangeMatch) {
    return (
      <>
        <Counter target={parseInt(rangeMatch[1], 10)} />–
        <Counter target={parseInt(rangeMatch[2], 10)} />
      </>
    );
  }
  if (numMatch) {
    const prefix = numMatch[1];
    const num = parseInt(numMatch[2].replace(/,/g, ""), 10);
    return (
      <>
        {prefix}
        <Counter target={num} />
      </>
    );
  }
  return value;
};

export default function Hero() {
  const { t } = useLang();
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative bg-black text-white overflow-hidden pt-28"
    >
      {/* Background video — subtle medical scene loop */}
      <video
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        src="https://videos.pexels.com/video-files/6130056/6130056-hd_1920_1080_30fps.mp4"
        poster="https://images.pexels.com/videos/6130056/pexels-photo-6130056.jpeg?auto=compress&cs=tinysrgb&w=1920"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0.85) 100%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 30%, rgba(0,82,255,0.25), transparent 60%), radial-gradient(40% 40% at 10% 90%, rgba(20,99,255,0.18), transparent 70%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 md:pt-28 pb-24 md:pb-36">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <p
              className="text-xs tracking-[0.24em] uppercase font-semibold text-blue-400 mb-6"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-7 h-px bg-blue-400 align-middle mr-3" />
              {t.hero.eyebrow}
            </p>
            <h1
              className="font-display font-bold tracking-tighter text-balance text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95]"
              data-testid="hero-title"
            >
              {t.hero.title}
            </h1>
            <p
              className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#quote"
                data-testid="hero-cta-primary"
                className="btn-primary group"
              >
                {t.hero.ctaPrimary}
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#products"
                data-testid="hero-cta-secondary"
                className="btn-outline-light group"
              >
                {t.hero.ctaSecondary}
                <ArrowDown
                  size={18}
                  className="transition-transform group-hover:translate-y-1"
                />
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-px bg-white/10 border border-white/10">
            {[
              { v: t.hero.stat1, l: t.hero.stat1Label, big: false, suffix: "" },
              {
                v: t.hero.stat2,
                l: t.hero.stat2Label,
                big: true,
                suffix: t.hero.stat2Suffix || "",
              },
              { v: t.hero.stat3, l: t.hero.stat3Label, big: true, suffix: "" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-black p-6 lg:p-8"
                data-testid={`hero-stat-${i}`}
              >
                <div
                  className={`font-display font-bold tracking-tighter ${
                    s.big ? "text-3xl lg:text-5xl" : "text-2xl lg:text-3xl"
                  }`}
                >
                  {i === 1 ? (
                    <>
                      {renderStat(s.v)}
                      {s.suffix}
                    </>
                  ) : (
                    renderStat(s.v)
                  )}
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-white/50 mt-2">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent" />
    </section>
  );
}
